const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * How long ago (or how far ahead) an instant is, in three units and no
 * coarser: minutes under an hour, hours under a day, then days forever.
 *
 * Distinct from `formatTimeAgo`, which rolls days up into weeks, months, and
 * years and only looks backwards. Both exist on purpose: a reader skimming
 * their own history wants "3 months ago", while an operator comparing a row
 * against a deploy log or another row wants "412 days ago" — a number they can
 * subtract. This one also runs forwards, for a renewal or a scheduled switch.
 *
 * `now` is injectable for deterministic tests.
 */
export default function formatElapsed(instant: string | Date, now: Date = new Date()): string | null {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const diffMs = now.getTime() - date.getTime();
  const absMs = Math.abs(diffMs);
  if (absMs < MS_PER_MINUTE) {
    return 'just now';
  }
  const [amount, unit] =
    absMs < MS_PER_HOUR
      ? [Math.floor(absMs / MS_PER_MINUTE), 'minute']
      : absMs < MS_PER_DAY
        ? [Math.floor(absMs / MS_PER_HOUR), 'hour']
        : [Math.floor(absMs / MS_PER_DAY), 'day'];
  const phrase = `${amount} ${unit}${amount === 1 ? '' : 's'}`;
  return diffMs >= 0 ? `${phrase} ago` : `in ${phrase}`;
}
